/**
 * Color Validation Utilities
 *
 * Provides functions for validating QR code colors against WCAG contrast guidelines.
 * The primary use case is ensuring QR code foreground colors have sufficient contrast
 * against a white background for reliable scanning.
 *
 * WCAG Contrast Calculation:
 * 1. Convert hex to RGB
 * 2. Linearize sRGB values: if sRGB <= 0.03928 then linear = sRGB/12.92 else linear = ((sRGB+0.055)/1.055)^2.4
 * 3. Calculate relative luminance: L = 0.2126 * R + 0.7152 * G + 0.0722 * B
 * 4. Contrast ratio = (L1 + 0.05) / (L2 + 0.05) where L1 is the lighter luminance
 *
 * Requirements: 3.5 - Validate color provides sufficient contrast against white for scannability
 */

/**
 * RGB color representation
 */
export interface RgbColor {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/**
 * Minimum contrast ratio required for QR code scannability (WCAG non-text elements)
 */
export const MIN_CONTRAST_RATIO = 3;

/**
 * White color in hex format (QR code background)
 */
export const WHITE_HEX = '#FFFFFF';

/**
 * Converts a hex color string to RGB values.
 *
 * @param hex - Hex color string (with or without # prefix, 3 or 6 characters)
 * @returns RGB color object with r, g, b values (0-255)
 * @throws Error if hex format is invalid
 *
 * @example
 * hexToRgb('#FF0000') // { r: 255, g: 0, b: 0 }
 * hexToRgb('000')     // { r: 0, g: 0, b: 0 }
 */
export function hexToRgb(hex: string): RgbColor {
  // Remove # prefix if present
  const cleanHex = hex.replace(/^#/, '');

  // Validate hex format
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    throw new Error(`Invalid hex color format: ${hex}`);
  }

  // Expand 3-character hex to 6-character
  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((c) => c + c)
          .join('')
      : cleanHex;

  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  return { r, g, b };
}

/**
 * Linearizes an sRGB color channel value for luminance calculation.
 *
 * Per WCAG 2.1 specification:
 * - If sRGB <= 0.03928, linear = sRGB / 12.92
 * - Otherwise, linear = ((sRGB + 0.055) / 1.055) ^ 2.4
 *
 * @param srgbChannel - sRGB channel value (0-1 range)
 * @returns Linearized channel value
 */
function linearizeChannel(srgbChannel: number): number {
  if (srgbChannel <= 0.03928) {
    return srgbChannel / 12.92;
  }
  return Math.pow((srgbChannel + 0.055) / 1.055, 2.4);
}

/**
 * Calculates the relative luminance of a color per WCAG 2.1 specification.
 *
 * Formula: L = 0.2126 * R + 0.7152 * G + 0.0722 * B
 * where R, G, B are linearized sRGB values.
 *
 * @param r - Red channel (0-255)
 * @param g - Green channel (0-255)
 * @param b - Blue channel (0-255)
 * @returns Relative luminance (0-1 range)
 *
 * @example
 * calculateLuminance(255, 255, 255) // 1.0 (white)
 * calculateLuminance(0, 0, 0)       // 0.0 (black)
 */
export function calculateLuminance(r: number, g: number, b: number): number {
  // Convert 0-255 to 0-1 range
  const sR = r / 255;
  const sG = g / 255;
  const sB = b / 255;

  // Linearize each channel
  const linearR = linearizeChannel(sR);
  const linearG = linearizeChannel(sG);
  const linearB = linearizeChannel(sB);

  // Calculate luminance using WCAG coefficients
  return 0.2126 * linearR + 0.7152 * linearG + 0.0722 * linearB;
}

/**
 * Calculates the contrast ratio between two colors per WCAG 2.1 specification.
 *
 * Formula: (L1 + 0.05) / (L2 + 0.05)
 * where L1 is the lighter luminance and L2 is the darker luminance.
 *
 * @param color1 - First color in hex format
 * @param color2 - Second color in hex format
 * @returns Contrast ratio (1:1 to 21:1 range)
 *
 * @example
 * calculateContrastRatio('#000000', '#FFFFFF') // 21 (maximum contrast)
 * calculateContrastRatio('#FFFFFF', '#FFFFFF') // 1 (no contrast)
 */
export function calculateContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const luminance1 = calculateLuminance(rgb1.r, rgb1.g, rgb1.b);
  const luminance2 = calculateLuminance(rgb2.r, rgb2.g, rgb2.b);

  // Ensure L1 is the lighter luminance
  const lighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Validates that a color provides sufficient contrast against white for QR code scannability.
 *
 * Per WCAG guidelines for non-text elements, a minimum contrast ratio of 3:1 is required.
 * This ensures the QR code can be reliably scanned by various devices.
 *
 * @param color - Color to validate in hex format
 * @returns true if the color has at least 3:1 contrast ratio against white
 *
 * @example
 * validateColorContrast('#000000') // true (black has 21:1 contrast)
 * validateColorContrast('#EEEEEE') // false (light gray has insufficient contrast)
 *
 * Requirements: 3.5 - Validate color provides sufficient contrast against white
 */
export function validateColorContrast(color: string): boolean {
  const contrastRatio = calculateContrastRatio(color, WHITE_HEX);
  return contrastRatio >= MIN_CONTRAST_RATIO;
}
